import {
  HiOutlineHomeModern,
  HiOutlineUsers,
  HiOutlineBriefcase,
  HiOutlineCurrencyDollar,
  HiOutlineChartBarSquare,
} from 'react-icons/hi2';
import type { OrgStats } from '../../api/analytics';
import { StatCard } from '../shared/StatCard';
import { ChartCard } from '../charts/ChartCard';
import { CostDonutChart } from '../charts/CostDonutChart';
import { ComparisonBarChart } from '../charts/ComparisonBarChart';
import { DataTable } from '../shared/DataTable';
import type { Column } from '../shared/DataTable';
import { buildCostDonutData } from '../charts/chartTheme';
import { fmt, fmtCHF, fmtM } from '../../utils/format';
import type { ProjectSummary } from '../../api/analytics';
import styles from './OrgAnalyticsPage.module.css';

interface Props {
  data: OrgStats;
}

export function CommuneView({ data }: Props) {
  const donutData = buildCostDonutData(data.cost_breakdown);

  const sbpData = [
    {
      name: data.organization.name,
      sbp_logements: data.sbp_breakdown.logements || 0,
      sbp_activites: data.sbp_breakdown.activites || 0,
      sbp_equipements: data.sbp_breakdown.equipements || 0,
    },
  ];

  const projectBarData = data.projects.map((p) => ({
    name: p.name.length > 18 ? p.name.slice(0, 18) + '...' : p.name,
    logements: p.logements,
    emplois: p.emplois,
  }));

  const columns: Column<ProjectSummary>[] = [
    { key: 'name', label: 'Project' },
    { key: 'logements', label: 'Logements', align: 'right', render: (r) => fmt(r.logements) },
    { key: 'emplois', label: 'Emplois', align: 'right', render: (r) => fmt(r.emplois) },
    { key: 'ha_total', label: 'Ha', align: 'right', render: (r) => r.ha_total ? r.ha_total.toFixed(1) : '\u2014' },
  ];

  const inhabitants = Math.round(data.total_logements * 2.3);

  return (
    <>
      <div className={styles.statsRow}>
        <StatCard label="Logements" value={fmt(data.total_logements)} icon={<HiOutlineHomeModern />} />
        <StatCard label="Inhabitants" value={`~${fmt(inhabitants)}`} icon={<HiOutlineUsers />} />
        <StatCard label="Emplois" value={fmt(data.total_emplois)} icon={<HiOutlineBriefcase />} />
        <StatCard
          label="Total Cost"
          value={data.total_cost > 0 ? fmtCHF(data.total_cost) : '\u2014'}
          icon={<HiOutlineCurrencyDollar />}
          color="var(--color-primary)"
        />
        <StatCard
          label="Density"
          value={data.avg_density > 0 ? `${data.avg_density}` : '\u2014'}
          sub="logements/ha"
          icon={<HiOutlineChartBarSquare />}
        />
      </div>

      <div className={styles.chartsRow}>
        <ChartCard title="Cost Decomposition" subtitle="This commune's projects">
          <CostDonutChart
            data={donutData}
            centerValue={data.total_cost > 0 ? fmtM(data.total_cost) : undefined}
            centerLabel="Total"
          />
        </ChartCard>
        {projectBarData.length > 0 ? (
          <ChartCard title="Projects Comparison" subtitle="Logements & Emplois">
            <ComparisonBarChart
              data={projectBarData}
              metrics={[
                { key: 'logements', label: 'Logements', color: '#6366f1' },
                { key: 'emplois', label: 'Emplois', color: '#22d3ee' },
              ]}
              layout={projectBarData.length > 3 ? 'vertical' : 'horizontal'}
            />
          </ChartCard>
        ) : (
          <ChartCard title="SBP Breakdown" subtitle="Surface breakdown (m²)">
            <ComparisonBarChart
              data={sbpData}
              metrics={[
                { key: 'sbp_logements', label: 'Logements', color: '#6366f1' },
                { key: 'sbp_activites', label: 'Activités', color: '#22d3ee' },
                { key: 'sbp_equipements', label: 'Équipements', color: '#a78bfa' },
              ]}
              layout="horizontal"
              stacked
              formatValue={(v) => `${v.toLocaleString('fr-CH')} m²`}
            />
          </ChartCard>
        )}
      </div>

      {((data.sbp_breakdown.logements ?? 0) > 0 || (data.sbp_breakdown.activites ?? 0) > 0) && projectBarData.length > 0 && (
        <div className={styles.chartsRow}>
          <ChartCard title="SBP Breakdown" subtitle="Surface breakdown (m²)">
            <ComparisonBarChart
              data={sbpData}
              metrics={[
                { key: 'sbp_logements', label: 'Logements', color: '#6366f1' },
                { key: 'sbp_activites', label: 'Activités', color: '#22d3ee' },
                { key: 'sbp_equipements', label: 'Équipements', color: '#a78bfa' },
              ]}
              layout="horizontal"
              stacked
              formatValue={(v) => `${v.toLocaleString('fr-CH')} m²`}
            />
          </ChartCard>
          <div />
        </div>
      )}

      {data.projects.length > 0 && (
        <div className={styles.tableSection}>
          <h3>Projects</h3>
          <DataTable
            columns={columns}
            data={data.projects}
            keyField="id"
          />
        </div>
      )}
    </>
  );
}
